import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import ResizeSensor from 'resize-sensor';
import { map, Observable, ReplaySubject, Subject, Subscriber, tap } from 'rxjs';

import { AppConf, Logger, None, Option } from '@contact/upgrade';

export type LoaderFn = (frame: HTMLIFrameElement) => Observable<any>;

export type SensorCallback = () => void;

export interface ExtApplication extends ExtFrame {
  frameId: string;
  iframe: HTMLIFrameElement;
  resizeSensor: Option<any>;
  sensorCallback: Option<SensorCallback>;
  loaded: boolean;
  obs$: ReplaySubject<HTMLIFrameElement>;
  options: ExtApplicationOptions;
}

export interface ExtFrame {
  frameId: string;
  iframe: HTMLIFrameElement;
}

export interface ExtApplicationOptions {
  hiddenClassName?: string;
}

const DEFAULT_HIDDEN_CLASS_NAME = 'hidden';

@Injectable({
  providedIn: 'root',
})
export class AppExternalService {
  private iframes: { [name: string]: ExtApplication } = {};
  private subject = new Subject<ExtFrame>();
  private externalApplications: { [key: string]: string };
  private renderer: Renderer2;

  constructor(private readonly logger: Logger, private readonly appConf: AppConf, rendererFactory: RendererFactory2) {
    this.logger = this.logger.forSource(`AppExternalService`);

    this.appConf.subscribe(({ externalApplications }) => (this.externalApplications = externalApplications ?? {}));
    this.applicationAdded$().forEach(iframe => this.logger.info(`appConfAdded application named[${iframe.name}] URL[${iframe.src}]`));

    this.renderer = rendererFactory.createRenderer(null, null);
  }

  private initializeApp(name: string, url?: string, options?: ExtApplicationOptions): ExtApplication {
    const iframe = document.createElement('iframe');
    iframe.id = name;
    iframe.name = name;
    Option.of(url)
      .filter(s => s !== '')
      .forEach(s => (iframe.src = s));
    iframe.setAttribute('width', '0');
    iframe.setAttribute('height', '0');

    const app: ExtApplication = {
      frameId: name,
      iframe: iframe,
      resizeSensor: None,
      sensorCallback: None,
      loaded: false,
      obs$: new ReplaySubject<HTMLIFrameElement>(1),
      options: options || {},
    };

    this.iframes[name] = app;
    this.hideApplication(name);
    this.subject.next(app);
    return app;
  }

  private getApplication(name: string): Option<ExtApplication> {
    return Option.of(this.iframes[name]);
  }

  /**
   * Get application URL loaded from configuration file.
   * @param {string} name the name of the application.
   * @returns {Option<string>}
   */
  getUrlFromConfig(name: string): Option<string> {
    return Option.of(this.externalApplications).flatMap(config => Option.of(config[name]));
  }

  /**
   * Shows application's iframe corresponding to given name.
   * @param {string} name the name of the application to show.
   */
  showApplication(name: string): void {
    this.getApplication(name).map(extApp =>
      this.renderer.removeClass(extApp.iframe, extApp.options.hiddenClassName || DEFAULT_HIDDEN_CLASS_NAME)
    );
  }

  /**
   * Hides application's iframe corresponding to given name.
   * @param {string} name the name of the application to hide.
   */
  hideApplication(name: string): void {
    this.getApplication(name).map(extApp =>
      this.renderer.addClass(extApp.iframe, extApp.options.hiddenClassName || DEFAULT_HIDDEN_CLASS_NAME)
    );
  }

  /**
   * @param {string} name the name of the application.
   * @returns {boolean} true is the application corresponding to given name
   */
  isVisible(name: string): boolean {
    return this.getApplication(name)
      .map(extApp => extApp.iframe.className.indexOf(extApp.options.hiddenClassName || DEFAULT_HIDDEN_CLASS_NAME) === -1)
      .getOrElse(false);
  }

  /**
   * Get an observable which notifies application add.
   * @returns {Observable<HTMLIFrameElement>}
   */
  applicationAdded$(): Observable<HTMLIFrameElement> {
    return this.subject.pipe(map(app => app.iframe));
  }

  /**
   * Creates an empty application (empty iframe) with given name.
   * @param {string} name the name of the application which is iframe's identifier and name too.
   * @returns {Option<HTMLIFrameElement>} An option of the iframe containing the application.
   */
  createApplication(name: string): Option<HTMLIFrameElement> {
    if (this.getApplication(name).isEmpty()) {
      this.initializeApp(name);
    } else {
      this.logger.warn(`appConfAlready existing application named[${name}]`);
    }
    return this.getApplication(name).map(extApp => extApp.iframe);
  }

  /**
   * Adds application with given name. It creates an iframe which loads given URL if defined or configured URL in
   * 'configuration.json' in 'externalApplications' section corresponding to givent name.
   * @param {string} name the name of the application which is iframe's identifier and name too.
   * @param {string} [url] the URL of the application.
   */
  addApplication(name: string, url?: string): void {
    if (this.getApplication(name).isEmpty()) {
      Option.of(url)
        .orElseFunc(() => this.getUrlFromConfig(name))
        .forEach(s => this.initializeApp(name, s));
    } else {
      this.logger.warn(`appConfAlready added application named[${name}]`);
    }
  }

  /**
   * Removes application corresponding to given name. It removes the iframe containing the application.
   * @param {string} name the name of the application.
   */
  removeApplication(name: string): void {
    this.getApplication(name).forEach(app => {
      app.obs$.complete();
      app.obs$.unsubscribe();
      app.resizeSensor.forEach(sensor => app.sensorCallback.forEach(callback => sensor.detach(app.iframe, callback)));
      app.iframe.src = 'about:blank';
      if (app.iframe.contentWindow) {
        try {
          app.iframe.contentWindow.document.write('');
          app.iframe.contentWindow.close();
        } catch (e) {
          this.logger.warn(`Impossible to access frame's contents for cleaning for [${name}], probably cross-domain issues`);
        }
      }
      if (app.iframe.parentNode) {
        app.iframe.parentNode.removeChild(app.iframe);
      }
      delete this.iframes[name];
      this.logger.info(`appConfRemoved application named[${name}]`);
    });
  }

  /**
   * Get iframe containing the application corresponding to given name.
   * @param {string} name the name of the application.
   * @returns {Option<HTMLIFrameElement>}
   */
  getApplicationContainer(name: string): Option<HTMLIFrameElement> {
    return this.getApplication(name).map(extApp => extApp.iframe);
  }

  private computeIFramePosition(iFrame: HTMLIFrameElement, container: HTMLElement): void {
    const rect = container.getBoundingClientRect();
    iFrame.style.top = rect.top + 'px';
    iFrame.style.left = rect.left + 'px';
    iFrame.style.width = rect.width + 'px';
    iFrame.style.height = rect.height + 'px';
    iFrame.setAttribute('width', rect.width.toString());
    iFrame.setAttribute('height', rect.height.toString());
  }

  /**
   * Position (absolute) the iframe containing the application corresponding to given name into a given container.
   * @param {string} name the name of the application.
   * @param {HTMLElement} container
   */
  positionApplication(name: string, container: HTMLElement): void {
    if (container) {
      this.getApplication(name).forEach(extApp => {
        this.computeIFramePosition(extApp.iframe, container);
        const callback = () => this.computeIFramePosition(extApp.iframe, container);
        extApp.resizeSensor = Option.of(new ResizeSensor(container, callback));
        extApp.sensorCallback = Option.of(callback);
      });
    }
  }

  /**
   * Insert the iframe containing the application corresponding to given name into a given container.
   * @param {string} name the name of the application.
   * @param {HTMLElement} container
   */
  insertApplication(name: string, container: HTMLElement): void {
    if (container) {
      this.getApplication(name).forEach(extApp => {
        container.appendChild(extApp.iframe);
        extApp.iframe.style.width = '100%';
        extApp.iframe.style.height = '100%';
        extApp.iframe.style.border = 'none';
        extApp.iframe.setAttribute('width', '100%');
        extApp.iframe.setAttribute('height', '100%');
        extApp.iframe.setAttribute('border', '0');
      });
    }
  }

  /**
   * Reload application corresponding to given name.
   * @param {string} name the name of the application which is iframe's identifier and name too.
   * @param {string} [url] the URL of the application.
   * @param {boolean} if this boolean is true, the frame will be insert in dom only with given container if container is not given well we wait another call
   *                  if boolean is false, the frame will be insert by component ${ExternalApplicationsService.name}
   * @param {LoaderFn} callback function to wait for loading
   * @param {ExtApplicationOptions} options other options for the frame creation (e.g.: specific frame classes). Optional parameter
   * @returns { Observable<HTMLElement>}
   */
  createApp(
    frameId: string,
    url: string,
    lazy: boolean = false,
    waitForLoad?: LoaderFn,
    options?: ExtApplicationOptions
  ): Observable<HTMLElement> {
    // found iframe or initalize
    const app: ExtApplication = this.iframes[frameId] || this.initializeApp(frameId, url, options);
    // if frame not load or just wait until another load
    if (!app.loaded && !lazy) {
      app.loaded = true;

      // subscriber use to inform subscriber
      const subscriber: Subscriber<any> = Subscriber.create(
        undefined,
        err => app.obs$.error(err),
        () => {
          app.obs$.next(app.iframe);
          app.obs$.complete();
        }
      );

      // check wait for load callback
      Option.of(waitForLoad)
        // if callback present
        // we subscribe to callback with app owner rx subject
        // when wait for load is completed we inform rx subject
        // that frame is loaded
        .sideEffect(f => f(app.iframe).subscribe(subscriber))
        // else just inform rx subject
        .orSideEffect(() => subscriber.complete());
    }
    // return observable from rx subject whose emit value when onNext() called
    return app.obs$.pipe(tap({ error: () => this.removeApplication(app.frameId) }));
  }

  public getExternalApplications(): { [key: string]: string } {
    return this.externalApplications;
  }
}
